import { makeId, idPrefixes } from '@dashboard/services/uuid'
import type { Dimension, LogicDefinition, Segment } from '@dashboard/types/config'
import type { Partner } from '@dashboard/types/partner'
import { funnelDefinitionForEachFlow } from '@dashboard/modules/flow/services/funnelDefinitions'
import { getOtherSegmentForSegmentGroup } from '@dashboard/modules/segment/services/segmentUtilities'
import { useQuestionStore } from '@dashboard/modules/segment/stores/questionStore'
import { useSegmentGroupStore } from '@dashboard/modules/segment/stores/segmentGroupStore'
import { AnalyticsEventTypes, recordAnalyticsEvent } from '@dashboard/services/analytics'
import { useConfigStore } from '@dashboard/stores/configStore'
import {
  repairQuestions as repairConfigQuestions,
  repairFlowSplits as repairConfigFlowSplits,
  syncRouterSplitOrder as syncConfigRouterSplitOrder
} from '@shared/configRepairs'

export const createSegment = (segmentGroup: Dimension, name?: string, id?: string) => {
  const newSegmentId = makeId(
    idPrefixes.SEGMENT,
    segmentGroup.segments.map((s) => s.id)
  )

  const segment = {
    id: id || newSegmentId,
    name: name || 'New Segment'
  }

  const insertionIndex = segmentGroup.segments.length
  segmentGroup.segments.push(segment)

  const questionStore = useQuestionStore()
  const question = questionStore.getBySegmentGroupId(segmentGroup.id)
  if (question) {
    question.options.splice(insertionIndex, 0, {
      id: makeId(idPrefixes.RIGHT_ASK_QUESTION_OPTION),
      segmentId: id || newSegmentId,
      label: name || 'New Segment',
      conditions: {},
      scores: []
    })
  }

  funnelDefinitionForEachFlow((node) => {
    if (node.type === 'sg' && node.split && node.segmentGroupId === segmentGroup.id) {
      const defaultPathIndex = node.split.findIndex((path) => path.segmentId === null)
      const branchIndex = defaultPathIndex >= 0 ? defaultPathIndex : node.split.length
      node.split.splice(branchIndex, 0, {
        segmentId: segment.id,
        child: []
      })
    }
  })

  void recordAnalyticsEvent(AnalyticsEventTypes.SEGMENT_CREATED, {
    segmentId: segment.id,
    segmentName: segment.name,
    segmentGroupId: segmentGroup.id,
    segmentGroupName: segmentGroup.name
  })

  return segment
}

export const deleteSegment = (segment: Segment) => {
  const segmentGroupStore = useSegmentGroupStore()
  const segmentGroup = segmentGroupStore.segmentGroupForSegmentId(segment.id)
  if (!segmentGroup) {
    return
  }

  void recordAnalyticsEvent(AnalyticsEventTypes.SEGMENT_DELETED, {
    segmentId: segment.id,
    segmentName: segment.name,
    segmentGroupId: segmentGroup.id,
    segmentGroupName: segmentGroup.name
  })

  const idx = segmentGroup.segments.findIndex((s) => s.id === segment.id)
  if (idx >= 0) {
    segmentGroup.segments.splice(idx, 1)
  }

  const idx2 = segmentGroup.signals.findIndex((s) => s.indicates === segment.id)
  if (idx2 >= 0) {
    segmentGroup.signals.splice(idx2, 1)
  }

  funnelDefinitionForEachFlow((node) => {
    if (node.type === 'sg' && node.split) {
      node.split = node.split.filter((split) => split.segmentId !== segment.id)
    }
    // Clean up set_segment actions referencing this segment
    if (node.type === 'data' && node.actions) {
      node.actions = node.actions.filter((a: any) => !(a.type === 'set_segment' && a.segmentId === segment.id))
    }
  })

  // const configStore = useConfigStore()
  // const { config } = storeToRefs(configStore)
  // if (!config.value) return
  // // Create a new config object
  // const newConfig = klona(config.value)
  // // Find and update the segment group
  // const segmentGroupIndex = newConfig.dimensions?.findIndex((sg) =>
  //   sg.segments.some((s) => s.id === segment.id)
  // )
  // if (segmentGroupIndex === -1) return
  // const segmentGroup = newConfig.dimensions[segmentGroupIndex]
  // // Find and update the question
  // const questionIndex = newConfig.rightAsk?.questions?.findIndex(
  //   (q) => q.dimensionId === segmentGroup.id
  // )
  // // Remove the segment from question options if it exists
  // if (questionIndex !== -1 && newConfig.rightAsk?.questions) {
  //   const question = newConfig.rightAsk.questions[questionIndex]
  //   const optionIndex = question.options.findIndex(
  //     (option) => option.segmentId === segment.id
  //   )
  //   if (optionIndex >= 0) {
  //     question.options.splice(optionIndex, 1)
  //   }
  // }
  // // Remove from signals
  // const signalIndex = segmentGroup.signals.findIndex(
  //   (s) => s.indicates === segment.id
  // )
  // if (signalIndex >= 0) {
  //   segmentGroup.signals.splice(signalIndex, 1)
  // }
  // // Remove from segments
  // const segmentIndex = segmentGroup.segments.findIndex(
  //   (s) => s.id === segment.id
  // )
  // if (segmentIndex >= 0) {
  //   segmentGroup.segments.splice(segmentIndex, 1)
  // }
  // // Update the entire config object atomically
  // nextTick(() => {
  //   config.value = newConfig
  //   configStore.debouncedUpdateConfig(newConfig)
  // })
}

export const syncRouterSplitOrder = (segmentGroup: Dimension) => {
  syncConfigRouterSplitOrder(useConfigStore().config, segmentGroup)
}

export const toggleFavorite = (segmentGroup: Dimension) => {
  const segmentGroupStore = useSegmentGroupStore()
  const sg = segmentGroupStore.getById(segmentGroup.id)
  if (!sg) {
    return
  }
  sg.favorited = !sg.favorited
}

// Returns the matched inner AND condition (a live reference into the
// config tree) so callers can mutate it in place. Returning the enclosing
// OR group here would be a footgun — mixed AND groups like
// [userCondition, syncCondition] would cause callers writing to `and[0]`
// to overwrite the user-authored condition (ENG-1009).
const findSyncCondition = (definition: LogicDefinition, partner: Partner, syncToCustomField?: string | null) => {
  const orGroups = definition.or ?? []
  for (const orGroup of orGroups) {
    const match = orGroup.and?.find(
      (and) =>
        and.$type === 'customField' &&
        and.$source === partner.name &&
        // Mirror the Sync tab's canonical predicate
        // (segmentUtilities.ts:179-185 `autoSyncLogicDefinitionForSegment`):
        // match on $type + $source + operator='equals' + customFieldId,
        // ignoring value. The Sync tab's v-model writes user-customized
        // mapping values directly into this condition's `.value`, so a
        // value-based predicate wrongly rejects legacy customized conditions
        // (seen on vigeofit.com: "constant pain" etc.) and causes a duplicate
        // sync rule to be injected.
        //
        // Accept the explicit $managedBy marker first (canonical path), then
        // fall back to the Sync tab predicate for legacy backfill.
        (and.$managedBy === 'sync' ||
          (!!syncToCustomField && and.customFieldId === syncToCustomField && and.operator === 'equals'))
    )
    if (match) {
      return match
    }
  }
  return undefined
}

// Tag-mode sibling of findSyncCondition. Accepts either the explicit
// $managedBy marker OR the strict legacy-shape predicate:
//   $type === 'tags', $source matches partner, operator === 'includes',
//   single-element tagIds whose only entry matches the expected tag ID.
// The strict predicate is intentionally narrow so user-authored tag rules
// (e.g. multi-tag OR, different operator) are never false-positive-flagged.
const findTagSyncCondition = (definition: LogicDefinition, partner: Partner, expectedTagId?: string | null) => {
  const orGroups = definition.or ?? []
  for (const orGroup of orGroups) {
    const match = orGroup.and?.find(
      (and) =>
        and.$type === 'tags' &&
        and.$source === partner.name &&
        (and.$managedBy === 'sync' ||
          (!!expectedTagId &&
            and.operator === 'includes' &&
            Array.isArray(and.tagIds) &&
            and.tagIds.length === 1 &&
            and.tagIds[0] === expectedTagId))
    )
    if (match) {
      return match
    }
  }
  return undefined
}

// Remove only $managedBy:'sync' conditions of a specific $type/$source from
// a single signal. Used for both opposite-mode cleanup on mode-switch and
// per-segment cleanup when a tag mapping is removed.
const pruneSyncConditionsByType = (
  signal: Dimension['signals'][number],
  partner: Partner,
  type: 'customField' | 'tags'
) => {
  const orArr = signal.definition?.or
  if (!orArr?.length) {
    return
  }
  signal.definition.or = orArr
    .map((orGroup) => ({
      ...orGroup,
      and: (orGroup.and || []).filter((cond) => {
        const isSyncManaged = cond.$managedBy === 'sync' && cond.$type === type && cond.$source === partner.name
        return !isSyncManaged
      })
    }))
    .filter((orGroup) => (orGroup.and || []).length > 0)
}

export const signalForSegment = (segmentGroup: Dimension, segment: Segment) => {
  let signal = segmentGroup.signals.find((signal) => signal.indicates === segment.id)
  if (!signal) {
    signal = {
      indicates: segment.id,
      weight: '1',
      definition: { or: [] }
    }
    segmentGroup.signals.push(signal)
  }
  return signal
}

export const repairSegmentGroup = (segmentGroup: Dimension, { partner }: { partner?: Partner } = {}) => {
  // Clean up invalid sync configurations based on partner capabilities
  if (partner) {
    const syncMethods = partner.espFeatureSupport?.syncMethods || ['custom_field']

    // Check if current sync mode is invalid for this partner
    if (segmentGroup.syncMode === 'tag' && !syncMethods.includes('tag') && !syncMethods.includes('list')) {
      console.log(`Repairing: Removing invalid tag sync for ${partner.name} - only supports ${syncMethods.join(', ')}`)

      // Clear invalid sync mode and related data
      segmentGroup.syncMode = 'field' // Reset to field mode
      segmentGroup.syncMappings = {} // Clear tag mappings

      // Clear syncTo if it was set for tag mode
      if (segmentGroup.syncTo === partner.name) {
        segmentGroup.syncTo = null
      }
    }

    // Check if field sync is configured but not supported
    if (segmentGroup.syncMode === 'field' && !syncMethods.includes('custom_field')) {
      console.log(`Repairing: Removing invalid field sync for ${partner.name}`)

      // Clear field sync data
      segmentGroup.syncToCustomField = null
      segmentGroup.syncTo = null

      // Switch to first available sync method if any
      if (syncMethods.includes('tag')) {
        segmentGroup.syncMode = 'tag'
        segmentGroup.syncMappings = {}
      } else if (syncMethods.includes('list')) {
        segmentGroup.syncMode = 'tag' // We use 'tag' mode for lists too
        segmentGroup.syncMappings = {}
      } else {
        // No valid sync methods, clear everything
        segmentGroup.syncMode = null
      }
    }
  }

  const questionStore = useQuestionStore()
  const question = questionStore.getBySegmentGroupId(segmentGroup.id)

  // Check if display type is dropdown but multi-select is enabled
  if (segmentGroup.isMultiWinning && question?.displayType === 'list') {
    console.log('Repairing: Resetting display type from dropdown to buttons for multi-select question')
    question.displayType = 'buttons'
  }
  if (question) {
    const otherSegment = getOtherSegmentForSegmentGroup(segmentGroup)
    if (question.otherOptions?.enabled) {
      if (!otherSegment) {
        createSegment(segmentGroup, 'INT_OTHER', makeId(idPrefixes.SEGMENT))
      }
      question.options.sort((a, b) => {
        const indexA = segmentGroup.segments.findIndex((s) => s.id === a.segmentId)
        const indexB = segmentGroup.segments.findIndex((s) => s.id === b.segmentId)
        return indexA - indexB
      })
    } else {
      if (otherSegment) {
        const idx = segmentGroup.segments.findIndex((s) => s.id === otherSegment.id)
        if (idx >= 0) {
          segmentGroup.segments.splice(idx, 1)
        }

        const idx2 = segmentGroup.signals.findIndex((s) => s.indicates === otherSegment.id)
        if (idx2 >= 0) {
          segmentGroup.signals.splice(idx2, 1)
        }

        const idx3 = question.options.findIndex((o) => o.segmentId === otherSegment.id)
        if (idx3 >= 0) {
          question.options.splice(idx3, 1)
        }
      }
    }

    question.options.forEach((option) => {
      if (!segmentGroup.segments.find((s) => s.id === option.segmentId)) {
        question.options = question.options.filter((o) => o.segmentId !== option.segmentId)
      }
    })
  }

  segmentGroup.segments.forEach((segment) => {
    signalForSegment(segmentGroup, segment)
  })

  segmentGroup.signals.forEach((signal) => {
    if (!segmentGroup.segments.find((s) => s.id === signal.indicates)) {
      segmentGroup.signals = segmentGroup.signals.filter((s) => s.indicates !== signal.indicates)
    }
  })

  if (!partner) {
    return
  }

  const syncMode = segmentGroup.syncMode || 'field'
  const syncMappings = segmentGroup.syncMappings || {}
  const hasFieldSync = syncMode === 'field' && !!segmentGroup.syncToCustomField
  const hasTagSync = syncMode === 'tag' && Object.keys(syncMappings).length > 0

  // No active sync → strip every sync-managed condition and exit.
  // Position-independent cleanup preserves user-authored rules regardless of
  // where they sit in definition.or, and works on mixed signals (sync + user
  // conditions together).
  if (!hasFieldSync && !hasTagSync) {
    segmentGroup.signals.forEach((signal) => {
      const orArr = signal.definition?.or
      if (!orArr?.length) {
        return
      }
      signal.definition.or = orArr
        .map((orGroup) => ({
          ...orGroup,
          and: (orGroup.and || []).filter((cond) => cond.$managedBy !== 'sync')
        }))
        .filter((orGroup) => (orGroup.and || []).length > 0)
    })
    return
  }

  // Mode-switch cleanup: prune sync-managed conditions from the opposite
  // mode before injecting for the current mode. Mirrors PHP's
  // pruneStaleSignalConditions in SegmentGroupApiService::generateSyncSignals.
  const staleType = syncMode === 'tag' ? 'customField' : 'tags'
  segmentGroup.signals.forEach((signal) => {
    pruneSyncConditionsByType(signal, partner, staleType)
  })

  segmentGroup.syncTo = partner.name

  if (hasTagSync) {
    segmentGroup.segments.forEach((segment) => {
      const signal = signalForSegment(segmentGroup, segment)
      if (!signal) {
        return
      }

      const tagId = syncMappings[segment.id]
      if (!tagId) {
        // No mapping for this segment — prune any tag sync condition for it.
        // Mirrors PHP's pruneSignalConditionsForSegment.
        pruneSyncConditionsByType(signal, partner, 'tags')
        return
      }

      const syncCondition = findTagSyncCondition(signal.definition, partner, tagId)
      if (!syncCondition) {
        if (!signal.definition.or) {
          signal.definition.or = []
        }
        signal.definition.or = [
          {
            and: [
              {
                $type: 'tags',
                $source: partner.name,
                operator: 'includes',
                tagIds: [tagId],
                $managedBy: 'sync'
              }
            ]
          },
          ...signal.definition.or
        ]
      } else {
        // Backfill/update: stamp the $managedBy marker and refresh tagIds
        // (handles legacy pre-marker configs and tag remapping). Mutates the
        // matched condition directly — not `and[0]` — so a mixed-AND group
        // with a user-authored rule at index 0 is not clobbered.
        syncCondition.tagIds = [tagId]
        syncCondition.$managedBy = 'sync'
      }
    })
    return
  }

  // Field mode
  segmentGroup.segments.forEach((segment) => {
    const signal = signalForSegment(segmentGroup, segment)
    if (!signal) {
      return
    }

    const syncCondition = findSyncCondition(signal.definition, partner, segmentGroup.syncToCustomField)
    if (!syncCondition) {
      if (!signal.definition.or) {
        signal.definition.or = []
      }
      // Default new sync conditions to empty-string value. The user is expected
      // to fill each per-segment mapping via the Sync tab input (or the AI
      // Generate button). Pre-filling with segment.name is wrong because:
      //  1) Question-type segments often have HTML-formatted names (e.g.
      //     "<strong>None</strong> Everyone sees the same website...") which
      //     make no sense as an ESP custom-field value.
      //  2) Even for plain-text segment names, ESPs store short tokens
      //     ("none", "basic") not human-facing labels, so the pre-fill almost
      //     always mismatches reality.
      //  3) An empty default makes the AI Generate button meaningful —
      //     otherwise users think the mappings are already set.
      signal.definition.or = [
        {
          and: [
            {
              $type: 'customField',
              $source: partner.name,
              operator: 'equals',
              customFieldId: segmentGroup.syncToCustomField,
              value: '',
              $managedBy: 'sync'
            }
          ]
        },
        ...signal.definition.or
      ]
    } else {
      // Stamp the $managedBy marker and refresh customFieldId (handles field
      // ID remapping). Explicit sync edits repair against the old destination
      // before changing it, so legacy conditions can be safely remapped or
      // removed without mutating config when the editor is merely opened.
      //
      // Mutates the matched condition directly — not `and[0]` — so a mixed-AND
      // group with a user-authored rule at index 0 is not clobbered (ENG-1009).
      //
      // Do NOT overwrite `value`. Field mode stores per-segment mapping values
      // directly in the condition's `.value` (written by SegmentGroupSync.vue's
      // v-model bound to `autoSyncLogicDefinitionForSegment(segment).value`).
      // Overwriting destroys user-authored mapping values — seen on vigeofit.com
      // where customized values like "constant pain" got rewritten to the full
      // segment-name label on every config load.
      syncCondition.customFieldId = segmentGroup.syncToCustomField
      syncCondition.$managedBy = 'sync'
    }
  })
}

export const repairQuestions = () => {
  repairConfigQuestions(useConfigStore().config)
}

/**
 * Reconcile flow sg node splits with actual dimension segments.
 * Catches out-of-band changes (e.g., segments added/removed via MCP)
 * where the flow router nodes weren't updated accordingly.
 */
export const repairFlowSplits = () => {
  repairConfigFlowSplits(useConfigStore().config)
}
